import React, { useCallback, useEffect, useState } from "react";
import http from "../../../services/http";
import { DebtBreadcrumb, DebtContainer, DebtTitle, BreadcrumbItem, BreadcrumbSeparator } from "../DebtManagement/styled/DebtManagementStyles";
import { MyBookingWrapper } from "../MyBooking/styled/DesStyled";
import { OrderInner } from "../Order/styled/OrderStyle";
import BusinessLicenceReview from "../Partner/component/BusinessLicenceReview";
import HangChoChuKy from "./HangChoChuKy";

/**
 * BUSINESS LICENCES — hàng chờ duyệt giấy phép kinh doanh (Quân 26/10/2026).
 *
 * Trước đây chỉ thấy giấy phép khi mở TỪNG trạm (Stations → Edit Station) ⇒ đo prod 26/10: 12 trạm đã
 * nộp từ 21/10, 0 trạm được duyệt; trạm không rút được tiền nên gửi ticket. Màn này liệt kê theo trạng
 * thái, bấm một dòng là mở NGAY khối duyệt có sẵn (BusinessLicenceReview) — không có luồng duyệt thứ hai.
 * Giao diện TIẾNG ANH như cả Admin (luật Quân 25/08).
 */

type Status = "pending" | "approved" | "rejected" | "all";
type Row = {
	/** 26/10/2026: "form" = đơn chưa ký (giấy phép nộp trước khi ký), "station" = trạm. */
	kind?: "form" | "station";
	id: number;
	name: string;
	representativeName: string | null;
	bankAccountHolder: string | null;
	destinationName: string | null;
	countryCode: string | null;
	status: "pending" | "approved" | "rejected";
	submittedAt: string | null;
	reviewedAt: string | null;
	note: string | null;
	fileCount: number;
};

const TABS: Array<{ id: Status; label: string }> = [
	{ id: "pending", label: "Waiting for review" },
	{ id: "rejected", label: "Rejected" },
	{ id: "approved", label: "Approved" },
	{ id: "all", label: "All" },
];
const TONE: Record<Row["status"], { bg: string; fg: string; label: string }> = {
	pending: { bg: "#FFFAEB", fg: "#B54708", label: "Waiting for review" },
	approved: { bg: "#ECFDF3", fg: "#067647", label: "Approved" },
	rejected: { bg: "#FEF3F2", fg: "#B42318", label: "Rejected" },
};
const cell: React.CSSProperties = { padding: "8px 10px", borderBottom: "1px solid #eaecf0", verticalAlign: "top", fontSize: 13.5 };

function ngay(v: string | null) {
	if (!v) return "—";
	const d = new Date(v);
	if (Number.isNaN(d.getTime())) return "—";
	const soNgay = Math.floor((Date.now() - d.getTime()) / 86400000);
	return `${d.toLocaleDateString("en-GB")}${soNgay >= 1 ? ` (${soNgay}d ago)` : " (today)"}`;
}

/** 26/10/2026 (Quân chốt): công tắc "Bắt có giấy phép đã duyệt mới cho ký" — mặc định TẮT, chỉ super admin đổi. */
type CongTac = { enabled: boolean; updatedAt: string | null; updatedBy: string | null; canEdit: boolean };
function LicenceBeforeSigningSwitch() {
	const [st, setSt] = useState<CongTac | null>(null);
	const [dangLuu, setDangLuu] = useState(false);
	const [loi, setLoi] = useState("");
	useEffect(() => {
		http
			.get("/utility/licence-before-signing-switch")
			.then(({ data }) => setSt(data?.data || null))
			.catch(() => setLoi("Could not load the switch."));
	}, []);
	if (!st && !loi) return null;
	const bat = st?.enabled === true;
	const doi = async () => {
		if (!st?.canEdit || dangLuu) return;
		const next = !bat;
		const canhBao = next
			? "Turn ON? New stations (not signed yet) must have an approved business licence before they can sign and go live."
			: "Turn OFF? New stations can sign right away; the licence is still required before payouts.";
		if (!window.confirm(canhBao)) return;
		setDangLuu(true);
		setLoi("");
		try {
			const { data } = await http.put("/utility/licence-before-signing-switch", { enabled: next });
			setSt(data?.data || null);
		} catch (e: any) {
			setLoi(e?.response?.data?.message || "Could not save. Please try again.");
		} finally {
			setDangLuu(false);
		}
	};
	return (
		<div
			data-testid="licence-before-signing-switch"
			style={{
				display: "flex",
				gap: 16,
				alignItems: "center",
				justifyContent: "space-between",
				flexWrap: "wrap",
				background: bat ? "#ECFDF3" : "#F9FAFB",
				border: `1px solid ${bat ? "#ABEFC6" : "#EAECF0"}`,
				borderRadius: 10,
				padding: "12px 16px",
				margin: "0 0 14px",
			}}>
			<div style={{ flex: "1 1 320px" }}>
				<div style={{ fontWeight: 700, color: "#101828" }}>Require an approved licence before signing</div>
				<div style={{ fontSize: 13, color: "#475467", marginTop: 2 }}>
					{loi ||
						(bat
							? "On — new stations must have an approved business licence before they can sign and go live."
							: "Off — new stations sign right away. The licence is still required before payouts.")}
				</div>
				<div style={{ fontSize: 12, color: "#98A2B3", marginTop: 2 }}>
					{st?.updatedAt
						? `Last changed ${new Date(st.updatedAt).toLocaleString()}${st.updatedBy ? ` by ${st.updatedBy}` : ""}`
						: "Never changed"}
					{st && !st.canEdit ? " · Only a super admin can change this" : ""}
				</div>
			</div>
			<button
				type="button"
				data-testid="licence-before-signing-toggle"
				disabled={!st?.canEdit || dangLuu}
				onClick={doi}
				style={{
					padding: "7px 16px",
					borderRadius: 999,
					border: 0,
					fontWeight: 700,
					cursor: st?.canEdit && !dangLuu ? "pointer" : "not-allowed",
					opacity: st?.canEdit ? 1 : 0.6,
					background: bat ? "#067647" : "#98A2B3",
					color: "#fff",
				}}>
				{bat ? "On" : "Off"}
			</button>
		</div>
	);
}

const BusinessLicences: React.FC = () => {
	const [tab, setTab] = useState<Status>("pending");
	const [rows, setRows] = useState<Row[]>([]);
	const [counts, setCounts] = useState<Record<string, number>>({});
	const [loading, setLoading] = useState(false);
	const [loi, setLoi] = useState("");
	const [mo, setMo] = useState<string | null>(null);

	const tai = useCallback(async () => {
		setLoading(true);
		setLoi("");
		try {
			const { data } = await http.get("/admin/business-licences", { params: { status: tab } });
			setRows(data?.data?.items || []);
			setCounts(data?.data?.counts || {});
		} catch (e: any) {
			setLoi(e?.response?.data?.message || "Could not load licences.");
		} finally {
			setLoading(false);
		}
	}, [tab]);
	useEffect(() => {
		tai();
	}, [tai]);

	return (
		<MyBookingWrapper>
			<OrderInner>
				<DebtContainer>
					<DebtBreadcrumb>
						<BreadcrumbItem as="a" href="/" style={{ textDecoration: "none" }}>
							{"Home"}
						</BreadcrumbItem>
						<BreadcrumbSeparator src="./static/img/direction-right.png" alt=">" />
						<BreadcrumbItem active>{"Business licences"}</BreadcrumbItem>
					</DebtBreadcrumb>
					<DebtTitle>Business licences</DebtTitle>
					<p style={{ margin: "0 0 14px", color: "#475467", fontSize: 14 }}>
						Stations cannot request payouts until their licence is approved. Open a row, check the 4 conditions
						(entity, signer, payout account, signing evidence) and approve or reject — the station gets an email
						and a portal notification.
					</p>
					<LicenceBeforeSigningSwitch />
					<HangChoChuKy />
					<div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
						{TABS.map((t) => {
							const n = t.id === "all" ? null : counts[t.id];
							const dang = tab === t.id;
							return (
								<button
									key={t.id}
									type="button"
									data-testid={`licence-tab-${t.id}`}
									onClick={() => {
										setTab(t.id);
										setMo(null);
									}}
									style={{
										padding: "7px 14px",
										borderRadius: 999,
										cursor: "pointer",
										border: dang ? "1px solid #1570ef" : "1px solid #d0d5dd",
										background: dang ? "#eff8ff" : "#fff",
										color: dang ? "#175cd3" : "#344054",
										fontWeight: 600,
										fontSize: 13,
									}}>
									{t.label}
									{n != null ? ` (${n})` : ""}
								</button>
							);
						})}
					</div>
					{loi ? <p style={{ color: "#B42318" }}>{loi}</p> : null}
					{loading && !rows.length ? <p style={{ color: "#667085" }}>Loading…</p> : null}
					{!loading && !rows.length && !loi ? <p style={{ color: "#667085" }}>Nothing here.</p> : null}
					{rows.length ? (
						<div style={{ overflowX: "auto", background: "#fff", border: "1px solid #eaecf0", borderRadius: 10 }}>
							<table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="licence-queue">
								<thead>
									<tr style={{ background: "#f9fafb", textAlign: "left" }}>
										<th style={cell}>Station</th>
										<th style={cell}>Status</th>
										<th style={cell}>Submitted</th>
										<th style={cell}>Representative</th>
										<th style={cell}>Bank account holder</th>
										<th style={cell}>Files</th>
										<th style={cell} />
									</tr>
								</thead>
								<tbody>
									{rows.map((r) => {
										const t = TONE[r.status] || TONE.pending;
										const khoa = `${r.kind === "form" ? "f" : "s"}${r.id}`;
										const dangMo = mo === khoa;
										return (
											<React.Fragment key={khoa}>
												<tr>
													<td style={cell}>
														{r.kind === "form" ? (
															<>
																<strong>Application #{r.id}</strong> {r.name}{" "}
																<span style={{ color: "#B54708", fontSize: 12, fontWeight: 600 }}>(not signed yet)</span>
															</>
														) : (
															<>
																<strong>#{r.id}</strong> {r.name}
															</>
														)}
														<div style={{ color: "#667085", fontSize: 12 }}>
															{[r.destinationName, r.countryCode].filter(Boolean).join(" · ")}
														</div>
													</td>
													<td style={cell}>
														<span style={{ background: t.bg, color: t.fg, borderRadius: 999, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>
															{t.label}
														</span>
														{r.status === "rejected" && r.note ? (
															<div style={{ color: "#667085", fontSize: 12, marginTop: 4, maxWidth: 260 }}>{r.note}</div>
														) : null}
													</td>
													<td style={cell}>{ngay(r.submittedAt)}</td>
													<td style={cell}>{r.representativeName || <span style={{ color: "#98A2B3" }}>(empty)</span>}</td>
													<td style={cell}>{r.bankAccountHolder || <span style={{ color: "#98A2B3" }}>(empty)</span>}</td>
													<td style={cell}>{r.fileCount}</td>
													<td style={cell}>
														<button
															type="button"
															data-testid={r.kind === "form" ? `licence-open-form-${r.id}` : `licence-open-${r.id}`}
															onClick={() => setMo(dangMo ? null : khoa)}
															style={{
																padding: "6px 12px",
																borderRadius: 6,
																cursor: "pointer",
																border: 0,
																background: dangMo ? "#667085" : "#1570ef",
																color: "#fff",
																whiteSpace: "nowrap",
															}}>
															{dangMo ? "Close" : "Review"}
														</button>
													</td>
												</tr>
												{dangMo ? (
													<tr>
														<td colSpan={7} style={{ ...cell, background: "#f9fafb" }}>
															{r.kind === "form" ? (
																<BusinessLicenceReview formId={String(r.id)} open onChanged={tai} />
															) : (
																<BusinessLicenceReview partnerId={String(r.id)} open onChanged={tai} />
															)}
														</td>
													</tr>
												) : null}
											</React.Fragment>
										);
									})}
								</tbody>
							</table>
						</div>
					) : null}
				</DebtContainer>
			</OrderInner>
		</MyBookingWrapper>
	);
};

export default BusinessLicences;
